import React, { useContext, useEffect, useState } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import { ScrollView, View } from 'react-native';
import FavoritesScreen from '../../screens/FavoritesScreen';
import { GlobalStateContext } from './GlobalStateProvider';
import LogInScreen from '../../screens/login_screen/LogInScreen';
import DataOps from '../../data/DataOps';
import ExploreScreenMain from '../../screens/explore_screen/ExploreScreenMain';
import useSEO, { SITE_URL, DEFAULT_KEYWORDS } from './useSEO';
import Sessions from '../session/Sessions';
import Header from './Header';
import Footer from './Footer';
import HostingMain from '../../screens/host_screen/HostingMain';
import HostingCreateCampStart from '../../screens/host_screen/HostingCreateCampStart';
import WizardScreen from '../../screens/host_screen/components/setupwizard/Wizard';
import CampScreenMain from '../../screens/camp_screen/CampScreenMain';
import CampHostEditButton from '../../screens/camp_screen/components/hosts/CampHostEditButton';
import SessionViewButton from '../../screens/camp_screen/components/hosts/SessionViewButton';
import CampSessionSwitchButton from '../../screens/explore_screen/components/CampSessionSwitchButton';
import AccountPage from '../../screens/account_screen/AccountScreenMain';
import PersonalInfoPage from '../../screens/account_screen/personal_info_screen/PersonalInfoPage';
import PaymentMethodPage from '../../screens/account_screen/PaymentMethodPage';
import AccountSettingsPage from '../../screens/account_screen/AccountSettingsPage';
import MessagesPage from '../../screens/messages_screen/MessagesScreenMain';
import NotificationsPage from '../../screens/notification_screen/NotificationScreenMain';
import CoachPublicProfile from '../../screens/coach_screen/CoachPublicProfile';
import MobileLandingScreen from '../../screens/MobileLandingScreen';
import HelpCenterScreen from '../../screens/HelpCenterScreen';
import ContactUsScreen from '../../screens/ContactUsScreen';
import CampSafetyScreen from '../../screens/CampSafetyScreen';
import PrivacyPolicyScreen from '../../screens/PrivacyPolicyScreen';
import AnnouncementsScreen from '../../screens/AnnouncementsScreen';
import InvestorsScreen from '../../screens/InvestorsScreen';
import CareersScreen from '../../screens/CareersScreen';
import SiteAnnouncementBanner from './SiteAnnouncementBanner';
import HostingResourcesScreen from '../../screens/HostingResourcesScreen';
import CommunityForumScreen from '../../screens/CommunityForumScreen';
import HostResponsibilitiesScreen from '../../screens/HostResponsibilitiesScreen';
import TutorialsScreen from '../../screens/TutorialsScreen';
import { getExploreRouteMeta, isExploreLikePath } from '../../utility/ExploreRouteUtils';
import { trackScreen } from '../../utility/telemetry';

const ROUTE_META: Record<string, { title: string; description: string; canonical: string; keywords?: string }> = {
    '/': {
        title: 'SnapCamp | Find & Book Kids Summer Camps Near You',
        description:
            'Discover and book the best kids summer camps near you. Browse camps by sport, age, and location. Find trusted, verified programs for kids.',
        canonical: `${SITE_URL}/`,
        keywords: DEFAULT_KEYWORDS,
    },
    '/explore': {
        title: 'Explore Kids Summer Camps | SnapCamp',
        description:
            'Browse hundreds of kids summer camps by sport, location, price, and age. Filter by gymnastics, soccer, art, STEM, music, and more.',
        canonical: `${SITE_URL}/explore`,
        keywords: DEFAULT_KEYWORDS,
    },
    '/help-center': {
        title: 'Help Center | SnapCamp',
        description:
            'Find answers to common questions about booking camps, managing registrations, payments, and using the SnapCamp platform.',
        canonical: `${SITE_URL}/help-center`,
    },
    '/contact-us': {
        title: 'Contact Us | SnapCamp',
        description:
            "Get in touch with the SnapCamp team. We're here to help with questions about camps, bookings, hosting, and your account.",
        canonical: `${SITE_URL}/contact-us`,
    },
    '/camp-safety': {
        title: 'Camp Safety Standards | SnapCamp',
        description:
            "Learn about SnapCamp's safety standards and how we vet every camp to ensure a trusted, secure experience for every child.",
        canonical: `${SITE_URL}/camp-safety`,
    },
    '/careers': {
        title: 'Careers at SnapCamp | Join Our Team',
        description:
            'Join the SnapCamp team and help parents find the best summer camps for their kids. View open positions across engineering, marketing, and operations.',
        canonical: `${SITE_URL}/careers`,
    },
    '/investors': {
        title: 'Investor Relations | SnapCamp',
        description:
            "Learn about SnapCamp's mission, growth trajectory, and investment opportunities in the kids summer camp marketplace.",
        canonical: `${SITE_URL}/investors`,
    },
    '/hosting-resources': {
        title: 'Hosting Resources for Camp Directors | SnapCamp',
        description:
            'Resources, guides, and tools for camp directors to grow their programs, manage registrations, and reach more families on SnapCamp.',
        canonical: `${SITE_URL}/hosting-resources`,
    },
    '/community-forum': {
        title: 'Community Forum | SnapCamp',
        description:
            'Connect with other camp parents and directors in the SnapCamp community. Share tips, ask questions, and find recommendations.',
        canonical: `${SITE_URL}/community-forum`,
    },
    '/host-responsibilities': {
        title: 'Host Responsibilities | SnapCamp',
        description:
            'Understand the responsibilities and guidelines for hosting camps on the SnapCamp marketplace to ensure a great experience for families.',
        canonical: `${SITE_URL}/host-responsibilities`,
    },
    '/tutorials': {
        title: 'Tutorials | SnapCamp',
        description:
            'Step-by-step tutorials for using SnapCamp as a parent or camp director. Learn how to book camps, manage sessions, and more.',
        canonical: `${SITE_URL}/tutorials`,
    },
    '/announcements': {
        title: 'Announcements | SnapCamp',
        description: 'Latest news, platform updates, and announcements from the SnapCamp team.',
        canonical: `${SITE_URL}/announcements`,
    },
    '/privacy-policy': {
        title: 'Privacy Policy | SnapCamp',
        description: 'Learn how SnapCamp collects, uses, and protects personal information for web and mobile app experiences.',
        canonical: `${SITE_URL}/privacy-policy`,
    },
    '/favorites': {
        title: 'My Favorite Camps | SnapCamp',
        description: 'View and manage your saved favorite camps on SnapCamp.',
        canonical: `${SITE_URL}/favorites`,
        noIndex: true,
    } as any,
    '/sessions': {
        title: 'My Sessions | SnapCamp',
        description: 'View and manage your registered camp sessions on SnapCamp.',
        canonical: `${SITE_URL}/sessions`,
        noIndex: true,
    } as any,
};

/** Applies SEO meta for the current static route. Dynamic routes (camps, coach) manage their own SEO. */
const RouteSEO: React.FC = () => {
    const location = useLocation();
    const meta = ROUTE_META[location.pathname] || getExploreRouteMeta(location.pathname);
    useSEO(meta ? {
        title: meta.title,
        description: meta.description,
        canonical: meta.canonical,
        keywords: (meta as any).keywords,
        noIndex: !!(meta as any).noIndex,
    } : {});
    return null;
};

const Navigator: React.FC = () => {
    const { user: user, showLoginModal, setShowLoginModal } = useContext(GlobalStateContext);

    const [isCampEditing, setIsCampEditing] = useState(false);
    const [isSessionView, setIsSessionView] = useState(() => {
        try {
            // Start with persisted value
            const stored = localStorage.getItem('campPageSessionView');
            let initial = stored ? JSON.parse(stored) : false;
            // If landing directly on a camp with modal/query params, force Sessions view immediately
            if (typeof window !== 'undefined') {
                const { pathname, search } = window.location || { pathname: '', search: '' };
                if (pathname.includes('/camps')) {
                    const params = new URLSearchParams(search || '');
                    const openModal = params.get('openModal');
                    const sid = params.get('sessionId');
                    if (openModal === 'camperView' || !!sid) {
                        initial = true;
                    }
                }
            }
            return initial;
        } catch {
            return false;
        }
    });
    const [isPersonalInfoEditing, setIsPersonalInfoEditing] = useState(false); // keep for now but we will stop using it for PersonalInfo specifically
    const [atTop, setAtTop] = useState(true);
    const [isCampHost, setCampHost] = useState(false);
    const [isLoggedIn, setIsLoggedIn] = useState(true);
    const [zoomedIn, setZoomedIn] = useState(false);
    const scrollViewRef = React.useRef<any>(null);

    const [bypassMobileLanding, setBypassMobileLanding] = useState(() => {
        try {
            return sessionStorage.getItem('bypassMobileLanding') === 'true';
        } catch {
            return false;
        }
    });

    const isMobile = typeof navigator !== 'undefined' && /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

    // Use global persisted map/list view preference
    const { isMapView: globalIsMapView, setIsMapView: setGlobalIsMapView } = useContext(GlobalStateContext);
    const isListView = !globalIsMapView;
    const setListView = (value: boolean) => {
        setGlobalIsMapView(!value);
    };
    const location = useLocation();
    const showNavTabs = isExploreLikePath(location.pathname);
    const isCampPage = location.pathname.includes('/camps');
    const isMainPage = isExploreLikePath(location.pathname);
    const isPersonalInfoPage = location.pathname.includes('/account/personal-info');

    useEffect(() => {
        trackScreen(location.pathname, location.search);
    }, [location.pathname, location.search]);

    useEffect(() => {
        const checkUserLoggedIn = async () => {
            if (user) {
                setIsLoggedIn(true);
                if (showLoginModal) {
                    setShowLoginModal(false);
                }
            } else {
                const userData = await DataOps.retrieveDataFromStorage('user');
                const loggedIn = userData !== null;
                setIsLoggedIn(loggedIn);
            }
        };

        const unused_response = checkUserLoggedIn();
    }, [user]);

    // Ensure camp page opens in Sessions view when navigated with modal-open params
    useEffect(() => {
        try {
            if (!isCampPage) return;
            const params = new URLSearchParams(location.search || '');
            const openModal = params.get('openModal');
            const sessionId = params.get('sessionId');
            if ((openModal === 'camperView' || !!sessionId) && !isSessionView) {
                setIsSessionView(true);
            }
        } catch { }
    }, [isCampPage, location.search]);

    useEffect(() => {
        try {
            localStorage.setItem('campPageSessionView', JSON.stringify(isSessionView));
        } catch (error) {
            console.error('Error saving campPageSessionView to storage:', error);
        }

        // Scroll to top when view changes
        if (scrollViewRef.current && isCampPage) {
            scrollViewRef.current.scrollTo({ y: 0, animated: true });
        }
    }, [isSessionView, isCampPage]);

    useEffect(() => {
        if (isCampEditing) {
            setIsCampEditing(false);
        }

        const onPersonalInfo = location.pathname.includes('/account/personal-info');
        if (!onPersonalInfo && isPersonalInfoEditing) {
            setIsPersonalInfoEditing(false);
        }

        // Personal Info no longer needs manual edit mode toggle
    }, [location.pathname]);

    useEffect(() => {
        if (scrollViewRef.current?.scrollTo) {
            scrollViewRef.current.scrollTo({ y: 0, animated: false });
        }

        if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
            window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
        }

        setAtTop(true);
    }, [location.pathname]);

    // Respond to validation events to exit edit mode when valid
    useEffect(() => {
        const onValidated = () => setIsCampEditing(false);
        window.addEventListener('campEditValidated', onValidated);
        return () => window.removeEventListener('campEditValidated', onValidated);
    }, []);

    const handleScroll = (event) => {
        const yOffset = event.nativeEvent.contentOffset.y;
        setAtTop(yOffset === 0);

        setZoomedIn(false);
    };

    if (isMobile && !bypassMobileLanding) {
        return (
            <MobileLandingScreen onContinue={() => {
                setBypassMobileLanding(true);
                sessionStorage.setItem('bypassMobileLanding', 'true');
            }} />
        );
    }

    return (
        <View style={{ flex: 1 }}>
            <Header
                showNavTabs={showNavTabs}
                isCampPage={isCampPage}
                atTop={atTop}
                zoomedIn={zoomedIn}
                setZoomedIn={setZoomedIn}
            />
            <SiteAnnouncementBanner />

            <ScrollView ref={scrollViewRef} style={{ flex: 1 }} onScroll={handleScroll} scrollEventThrottle={16}>
                <main style={styles.mainContent}>
                    <RouteSEO />
                    <Routes>
                        <Route path='/' element={<ExploreScreenMain isListView={isListView} setListView={setListView} />} />
                        <Route path='/explore' element={<ExploreScreenMain isListView={isListView} setListView={setListView} />} />
                        <Route path='/summer-camps/:locationSlug' element={<ExploreScreenMain isListView={isListView} setListView={setListView} />} />
                        <Route path='/kids-camps/:locationSlug' element={<ExploreScreenMain isListView={isListView} setListView={setListView} />} />
                        <Route path='/sessions' element={<Sessions />} />
                        <Route path='/favorites' element={<FavoritesScreen />} />
                        <Route path='/help-center' element={<HelpCenterScreen />} />
                        <Route path='/contact-us' element={<ContactUsScreen />} />
                        <Route path='/camp-safety' element={<CampSafetyScreen />} />
                        <Route path='/privacy-policy' element={<PrivacyPolicyScreen />} />
                        <Route path='/announcements' element={<AnnouncementsScreen />} />
                        <Route path='/investors' element={<InvestorsScreen />} />
                        <Route path='/careers' element={<CareersScreen />} />
                        <Route path='/hosting-resources' element={<HostingResourcesScreen />} />
                        <Route path='/community-forum' element={<CommunityForumScreen />} />
                        <Route path='/host-responsibilities' element={<HostResponsibilitiesScreen />} />
                        <Route path='/tutorials' element={<TutorialsScreen />} />

                        {/*Dynamic routes*/}
                        <Route path='/camps/:campId'
                            element={<CampScreenMain setCampHost={setCampHost} isEditing={isCampEditing} isSessionView={isSessionView} />} />
                        <Route path='/coach/:coachId' element={<CoachPublicProfile />} />

                        {/*Hosting routes*/}
                        <Route path='/hosting-main' element={<HostingMain />} />
                        <Route path='/hosting-manage-camp' element={<HostingMain />} />
                        <Route path='/hosting-create-camp' element={<HostingCreateCampStart />} />
                        <Route path='/hosting-create-camp/setup' element={<WizardScreen />} />

                        <Route path='/account' element={<AccountPage />} />
                        <Route path='/account/personal-info' element={<PersonalInfoPage isEditing={isPersonalInfoEditing} />} />
                        <Route path='/account/payments' element={<PaymentMethodPage />} />
                        <Route path='/account/settings' element={<AccountSettingsPage />} />
                        <Route path='/messages' element={<MessagesPage />} />
                        <Route path='/notifications' element={<NotificationsPage />} />
                    </Routes>
                </main>

                <Footer />
            </ScrollView>

            {/* Floating camp action buttons anchored bottom-center. */}
            {isCampPage && (
                <View style={{
                    position: 'fixed' as any,
                    left: 0,
                    right: 0,
                    bottom: 50,
                    alignItems: 'center',
                    zIndex: 999,
                }}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
                        <SessionViewButton isCampHost={isCampHost} isSessionView={isSessionView} setIsSessionView={setIsSessionView} />
                        {isCampHost && (
                            <CampHostEditButton
                                isEditing={isCampEditing}
                                setIsEditing={setIsCampEditing}
                                onToggle={async () => {
                                    if (!isCampEditing) {
                                        setIsCampEditing(true);
                                        return;
                                    }
                                    // Validate sessions before allowing save to exit edit mode
                                    try {
                                        const event = new CustomEvent('requestCampValidation');
                                        window.dispatchEvent(event);
                                    } catch { }
                                }}
                            />
                        )}
                    </View>
                </View>
            )}

            {/* Floating List/Map toggle button for explore page. */}
            {isMainPage && (
                <CampSessionSwitchButton isListView={isListView} setListView={setListView} />
            )}

            {/* Login modal for logged out users. */}
            <LogInScreen visible={showLoginModal} setVisible={setShowLoginModal} />
        </View>
    );
};

const styles = {
    container: {
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        width: '100vw',
    },
    navbar: {
        backgroundColor: '#ffffff',
        color: '#333333',
        padding: '15px 20px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '1px solid #e0e0e0',
        marginBottom: '10px',
    },
    navLink: {
        color: 'black',
        textDecoration: 'none',
        fontSize: '1rem',
    },
    mainContent: {
        flex: 1,
    },
};

export default Navigator;
